import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, DollarSign, TrendingUp, Users, FileText, MessageCircle, CheckCircle, Clock, MapPin, Send, Eye, BarChart3, ShoppingBag } from "lucide-react";
import EntityLink from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import EntityComms from "../components/shared/EntityComms";
import InvestmentPackage from "../components/shared/InvestmentPackage";
import InvestorMemoPanel from "../components/shared/InvestorMemoPanel";
import { Entity360Shell, FundingProgressBar, DocumentCard } from "../components/shared/OSComponents";
import { fmtUSD } from "../utils/currency";
import { useAuth } from "../context/AuthContext";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };

export default function Opportunity360Page({ oppId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);
  const isInvestor = authUser && authUser.role === "investor";

  useEffect(() => {
    axios.get(`${API}/entity/opportunity/${oppId}/360`, { headers }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [oppId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Opportunity not found</div>;

  const { overview, deal, commitments, qa, distributions, documents } = data;
  const crumbs = [...breadcrumbs, { type: "opportunity", id: oppId, label: `Opp ${oppId.slice(0, 8)}` }];

  const fundingState = overview.pct_funded >= 100 ? "fully_funded" : overview.pct_funded >= 75 ? "nearly_funded" : overview.status === "open" ? "open_for_commitments" : overview.status === "closed" ? "closed" : "approved_for_marketplace";

  const relatedEntities = [
    ...(!isInvestor && deal ? [{ type: "deal", id: deal.lead_id, title: `${LOAN_LABELS[deal.loan_type] || deal.loan_type} Deal`, subtitle: deal.property_address || deal.state }] : []),
    ...(!isInvestor && deal?.borrower_id ? [{ type: "borrower", id: deal.borrower_id, title: deal.borrower_name || "Borrower", subtitle: "" }] : []),
    ...commitments.slice(0, 5).map(c => ({ type: "investor", id: c.user_id, title: c.investor_name || "Investor", subtitle: fmtUSD(c.amount) })),
    ...documents.slice(0, 3).map(d => ({ type: "document", id: d.doc_id, title: d.original_filename, subtitle: d.doc_type })),
  ];

  const quickActions = [
    { id: "commit", label: "Commit Capital", description: "Make an investment commitment", icon: DollarSign, onClick: () => {} },
    ...(!isInvestor && deal ? [{ id: "view_deal", label: "View Parent Deal", icon: Eye, onClick: () => onNavigate("deal", deal.lead_id) }] : []),
    { id: "add_question", label: "Ask a Question", icon: MessageCircle, onClick: () => setTab("qa") },
    ...(isInternal ? [{ id: "generate_report", label: "Generate Report", icon: BarChart3, onClick: () => {} }] : []),
    ...(isInternal ? [{ id: "add_note", label: "Add Note", icon: Send, onClick: () => setTab("notes") }] : []),
  ];

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "ai_memo", label: "AI Memo" },
    { key: "investment_package", label: "Deal Package" },
    { key: "capital", label: "Capital Stack" },
    { key: "commitments", label: `Commitments (${commitments.length})` },
    { key: "qa", label: `Q&A (${qa.length})` },
    { key: "documents", label: `Documents (${documents.length})` },
    ...(distributions.length ? [{ key: "distributions", label: `Distributions (${distributions.length})` }] : []),
    { key: "messages", label: "Messages" },
    ...(isInternal ? [{ key: "notes", label: "Notes" }] : []),
  ];

  const kpis = [
    { label: "Target Raise", value: fmtUSD(overview.target_raise), icon: DollarSign },
    { label: "Capital Raised", value: fmtUSD(overview.raised), icon: TrendingUp, accent: true },
    { label: "Investors", value: overview.investor_count, icon: Users },
    { label: "Min Investment", value: fmtUSD(overview.min_investment), icon: DollarSign },
    { label: "Projected Yield", value: overview.projected_yield ? `${overview.projected_yield}%` : "—", icon: TrendingUp },
  ];

  const oppType = overview.opportunity_type;

  const badges = [
    ...(oppType ? [{ label: oppType, className: "bg-indigo-100 text-indigo-600" }] : []),
    ...(deal ? [{ label: LOAN_LABELS[deal.loan_type] || deal.loan_type, className: "bg-blue-100 text-blue-600" }] : []),
    ...(deal?.state ? [{ label: deal.state, className: "bg-[#faf7f2] text-[#888]" }] : []),
  ];

  return (
    <Entity360Shell type="opportunity"
      title={oppType || (deal ? `${LOAN_LABELS[deal.loan_type] || deal.loan_type} — ${deal.state}` : `Opportunity ${oppId.slice(0, 8)}`)}
      subtitle={[!isInvestor && deal?.property_address, isInternal && deal?.borrower_name && `Borrower: ${deal.borrower_name}`, `Created ${overview.created_at ? new Date(overview.created_at).toLocaleDateString() : ""}`].filter(Boolean).join(" | ")}
      status={fundingState} badges={badges} kpis={kpis}
      tabs={tabs} activeTab={tab} onTabChange={setTab}
      onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity}
      quickActions={quickActions} relatedEntities={relatedEntities}>

      {tab === "overview" && (
        <div className="space-y-4">
          <FundingProgressBar raised={overview.raised} target={overview.target_raise} label="Funding Progress" />
          {deal && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Deal Snapshot</h3>
                <div className="space-y-2 text-xs">
                  {[
                    ...(oppType ? [["Opportunity Type", oppType]] : []),
                    ["Loan Type", LOAN_LABELS[deal.loan_type] || deal.loan_type],
                    ["State", deal.state],
                    ["Stage", deal.stage?.replace(/_/g, ' ')],
                    ["Purchase Price", deal.purchase_price ? fmtUSD(deal.purchase_price) : null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="text-[#888]">{k}</span><span className="font-semibold">{v}</span></div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Investment Terms</h3>
                <div className="space-y-2 text-xs">
                  {[
                    ["Target Raise", fmtUSD(overview.target_raise)],
                    ["Min Investment", fmtUSD(overview.min_investment)],
                    ["Projected Yield", overview.projected_yield ? `${overview.projected_yield}%` : null],
                    ["Term", overview.projected_term_months ? `${overview.projected_term_months} months` : null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="text-[#888]">{k}</span><span className={k === "Projected Yield" ? "font-bold text-emerald-600" : "font-semibold"}>{v}</span></div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {overview.publish_notes && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 md:col-span-2">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Investment Notes</h3>
              <p className="text-sm text-[#444] leading-relaxed">{overview.publish_notes}</p>
            </div>
          )}
        </div>
      )}

      {tab === "ai_memo" && (
        <InvestorMemoPanel leadId={deal?.lead_id} opportunityId={oppId} />
      )}

      {tab === "capital" && (
        <div className="space-y-4">
          <FundingProgressBar raised={overview.raised} target={overview.target_raise} label="Capital Stack" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 text-center">
              <div className="text-lg font-black text-[#222]">{fmtUSD(overview.target_raise)}</div><div className="text-[9px] text-[#999] uppercase">Target</div>
            </div>
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 text-center">
              <div className="text-lg font-black text-emerald-600">{fmtUSD(overview.raised)}</div><div className="text-[9px] text-[#999] uppercase">Raised</div>
            </div>
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 text-center">
              <div className="text-lg font-black text-amber-600">{fmtUSD(Math.max(0, overview.target_raise - overview.raised))}</div><div className="text-[9px] text-[#999] uppercase">Remaining</div>
            </div>
          </div>
          {commitments.length > 0 && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Allocation</h3>
              {commitments.map(c => (
                <div key={c.commitment_id} className="flex items-center justify-between py-2 border-b border-[#f0ebe0] last:border-0">
                  <EntityLink type="investor" id={c.user_id} label={c.investor_name || "Investor"} onClick={navigateToEntity} />
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#222]">{fmtUSD(c.amount)}</span>
                    <span className="text-[9px] text-[#999]">({((c.amount / overview.target_raise) * 100).toFixed(0)}%)</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "commitments" && (
        <div className="space-y-2">
          {commitments.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No commitments yet.</div> : commitments.map(c => (
            <div key={c.commitment_id} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center justify-between cursor-pointer hover:shadow-sm" onClick={() => onNavigate("commitment", c.commitment_id)}>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">{c.investor_name ? c.investor_name.charAt(0) : "$"}</div>
                <div>{c.investor_name ? <EntityLink type="investor" id={c.user_id} label={c.investor_name} onClick={navigateToEntity} /> : <span className="text-xs font-medium text-[#333]">Investor</span>}<div className="flex gap-2 text-[10px] text-[#999]">{c.express_commit && <span className="text-amber-600 font-medium">Express</span>}<span>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span></div></div>
              </div>
              <div className="flex items-center gap-3"><span className="text-sm font-bold text-[#222]">{fmtUSD(c.amount)}</span><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${c.status === "pending" ? "bg-amber-100 text-amber-600" : c.status === "approved" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-600"}`}>{c.status}</span></div>
            </div>
          ))}
        </div>
      )}

      {tab === "qa" && (
        <div className="space-y-2">
          {qa.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No Q&A yet.</div> : qa.map(q => (
            <div key={q.qa_id} className="rounded-xl border border-[#e8e0d0] bg-white p-3">
              <div className="flex items-start gap-2"><MessageCircle className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" /><div className="flex-1"><p className="text-sm text-[#333]">{q.question}</p>{q.answer && <div className="mt-2 pl-3 border-l-2 border-emerald-300"><p className="text-sm text-[#555]">{q.answer}</p><div className="flex items-center gap-1.5 mt-1 text-[9px] text-emerald-600"><CheckCircle className="h-3 w-3" />{q.answered_by_name || "Team"}</div></div>}{!q.answer && <div className="mt-1 flex items-center gap-1 text-[10px] text-[#bbb]"><Clock className="h-3 w-3" />Awaiting response</div>}</div></div>
            </div>
          ))}
        </div>
      )}

      {tab === "documents" && (
        <div className="space-y-2">
          {documents.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No documents.</div> : documents.map(d => <DocumentCard key={d.doc_id} doc={d} onClick={navigateToEntity} />)}
        </div>
      )}

      {tab === "distributions" && (
        <div className="space-y-2">
          {distributions.map((d, i) => (
            <div key={`dist-${i}`} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-600">{fmtUSD(d.amount)}</span>
              <div className="flex gap-2 text-[10px] text-[#999]">{d.dist_type && <span>{d.dist_type}</span>}<span>{d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}</span></div>
            </div>
          ))}
        </div>
      )}

      {tab === "investment_package" && (
        <InvestmentPackage oppId={oppId} oppData={data} onNavigate={navigateToEntity} />
      )}

      {tab === "messages" && (
        <EntityComms entityType="opportunity" entityId={oppId} />
      )}

      {tab === "notes" && <EntityNotes entityType="opportunity" entityId={oppId} />}
    </Entity360Shell>
  );
}
